/**
 * Target Manager for Aim Assist Training Lab
 * Procedural target archetypes (Sphere, Combat Bot, Drone, Disc),
 * dynamic kinematic motion patterns, and lifecycle recycling
 */

import * as THREE from 'three';
import { TARGET_COLORS } from './constants.js';

export class TargetManager {
  constructor(targetsGroup, settingsStore, sceneManager) {
    this.group = targetsGroup;
    this.settingsStore = settingsStore;
    this.sceneManager = sceneManager;

    this.targets = [];
    this.activeTargets = [];
    this.nextTargetId = 1;
    this.targetRadius = 0.40; // Base target radius in meters
    this.colorScheme = this.getColorScheme();

    settingsStore.subscribe(() => {
      this.colorScheme = this.getColorScheme();
      this.updateActiveMaterials();
    });
  }

  getColorScheme() {
    const colorId = this.settingsStore.get('targetColor') || 'cyan';
    const found = TARGET_COLORS.find((c) => c.id === colorId) || TARGET_COLORS[0];
    const isHighContrast = this.settingsStore.get('highContrast');
    if (isHighContrast) {
      return { core: 0xff2222, outer: 0xffffff, accent: 0xffdd00, hex: 0xff2222 };
    }
    return {
      core: parseInt(found.core.replace('#', '0x'), 16),
      outer: parseInt(found.outer.replace('#', '0x'), 16),
      accent: 0xffffff,
      hex: parseInt(found.core.replace('#', '0x'), 16)
    };
  }

  updateActiveMaterials() {
    for (const t of this.activeTargets) {
      if (t.coreMesh && t.outerMesh) {
        t.coreMesh.material.color?.setHex(this.colorScheme.core);
        t.outerMesh.material.color?.setHex(this.colorScheme.outer);
      }
    }
  }

  /**
   * Archetype 1: Cyber Orb Target
   * Glowing core + outer orbital ring
   */
  createSphereTarget(radius) {
    const group = new THREE.Group();

    // Inner Bullseye Core
    const coreGeo = new THREE.SphereGeometry(radius * 0.46, 20, 20);
    const coreMat = new THREE.MeshBasicMaterial({ color: this.colorScheme.core });
    const coreMesh = new THREE.Mesh(coreGeo, coreMat);
    coreMesh.name = 'target_core';
    group.add(coreMesh);

    // Center Bullseye Dot
    const dotGeo = new THREE.SphereGeometry(radius * 0.16, 12, 12);
    const dotMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dotMesh = new THREE.Mesh(dotGeo, dotMat);
    group.add(dotMesh);

    // Outer Torus Ring
    const outerGeo = new THREE.TorusGeometry(radius * 0.82, radius * 0.16, 14, 28);
    const outerMat = new THREE.MeshStandardMaterial({
      color: this.colorScheme.outer,
      roughness: 0.25,
      metalness: 0.3
    });
    const outerMesh = new THREE.Mesh(outerGeo, outerMat);
    outerMesh.name = 'target_outer';
    group.add(outerMesh);

    // Collider
    const hitGeo = new THREE.SphereGeometry(radius, 14, 14);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.name = 'target_collider';
    group.add(hitMesh);

    group.userData = { coreMesh, outerMesh, hitMesh, baseRadius: radius, shape: 'sphere' };
    return group;
  }

  /**
   * Archetype 2: Tactical Combat Bot
   * Capsule humanoid body + head with glowing visor (Headshot Zone)
   */
  createBotTarget(radius) {
    const group = new THREE.Group();

    // 1. Torso Body (Capsule / Cylinder)
    const torsoGeo = new THREE.CylinderGeometry(radius * 0.45, radius * 0.5, radius * 1.3, 16);
    const torsoMat = new THREE.MeshStandardMaterial({
      color: this.colorScheme.outer,
      roughness: 0.35,
      metalness: 0.5
    });
    const torsoMesh = new THREE.Mesh(torsoGeo, torsoMat);
    torsoMesh.position.y = -radius * 0.3;
    group.add(torsoMesh);

    // 2. Head / Critical Bullseye Sphere (Headshot Zone!)
    const headRadius = radius * 0.38;
    const headGeo = new THREE.SphereGeometry(headRadius, 16, 16);
    const headMat = new THREE.MeshBasicMaterial({ color: this.colorScheme.core });
    const headMesh = new THREE.Mesh(headGeo, headMat);
    headMesh.name = 'target_core';
    headMesh.position.y = radius * 0.65;
    group.add(headMesh);

    // Glowing Visor Strip on Head
    const visorGeo = new THREE.BoxGeometry(headRadius * 1.3, headRadius * 0.28, headRadius * 0.7);
    const visorMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const visorMesh = new THREE.Mesh(visorGeo, visorMat);
    visorMesh.position.set(0, radius * 0.65, headRadius * 0.5);
    group.add(visorMesh);

    // Collider encompassing the entire bot
    const hitGeo = new THREE.BoxGeometry(radius * 1.2, radius * 2.2, radius * 1.2);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.name = 'target_collider';
    group.add(hitMesh);

    group.userData = {
      coreMesh: headMesh,
      outerMesh: torsoMesh,
      hitMesh,
      baseRadius: radius,
      shape: 'bot',
      hasHeadshot: true
    };
    return group;
  }

  /**
   * Archetype 3: Stealth Drone (Octahedral Diamond)
   * Geometric diamond body with rotating orbital wings
   */
  createDroneTarget(radius) {
    const group = new THREE.Group();

    // Diamond Octahedron Core
    const droneGeo = new THREE.OctahedronGeometry(radius * 0.65, 0);
    const droneMat = new THREE.MeshBasicMaterial({ color: this.colorScheme.core });
    const droneMesh = new THREE.Mesh(droneGeo, droneMat);
    droneMesh.name = 'target_core';
    group.add(droneMesh);

    // Rotating Outer Ring Wings
    const ringGeo = new THREE.TorusGeometry(radius * 0.95, radius * 0.08, 8, 24);
    const ringMat = new THREE.MeshStandardMaterial({
      color: this.colorScheme.outer,
      roughness: 0.2,
      metalness: 0.6
    });
    const ringMesh = new THREE.Mesh(ringGeo, ringMat);
    ringMesh.name = 'target_outer';
    group.add(ringMesh);

    // Collider
    const hitGeo = new THREE.SphereGeometry(radius * 1.1, 12, 12);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.name = 'target_collider';
    group.add(hitMesh);

    group.userData = { coreMesh: droneMesh, outerMesh: ringMesh, hitMesh, baseRadius: radius, shape: 'drone' };
    return group;
  }

  /**
   * Archetype 4: Precision Concentric Disc
   * 3-tier scoring disc (Outer, Middle, Bullseye)
   */
  createDiscTarget(radius) {
    const group = new THREE.Group();

    // Outer Band
    const outerGeo = new THREE.CylinderGeometry(radius, radius, 0.08, 32);
    const outerMat = new THREE.MeshStandardMaterial({ color: this.colorScheme.outer, roughness: 0.4 });
    const outerMesh = new THREE.Mesh(outerGeo, outerMat);
    outerMesh.rotation.x = Math.PI / 2;
    group.add(outerMesh);

    // Middle Ring
    const midGeo = new THREE.CylinderGeometry(radius * 0.65, radius * 0.65, 0.09, 32);
    const midMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3 });
    const midMesh = new THREE.Mesh(midGeo, midMat);
    midMesh.rotation.x = Math.PI / 2;
    group.add(midMesh);

    // Inner Bullseye Core
    const bullseyeGeo = new THREE.CylinderGeometry(radius * 0.32, radius * 0.32, 0.1, 32);
    const bullseyeMat = new THREE.MeshBasicMaterial({ color: this.colorScheme.core });
    const bullseyeMesh = new THREE.Mesh(bullseyeGeo, bullseyeMat);
    bullseyeMesh.rotation.x = Math.PI / 2;
    bullseyeMesh.name = 'target_core';
    group.add(bullseyeMesh);

    // Collider
    const hitGeo = new THREE.SphereGeometry(radius * 1.05, 12, 12);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.name = 'target_collider';
    group.add(hitMesh);

    group.userData = { coreMesh: bullseyeMesh, outerMesh, hitMesh, baseRadius: radius, shape: 'disc' };
    return group;
  }

  /**
   * Archetype 5: Humanoid Tactical Dummy (Person Silhouette)
   * Anatomically proportioned competitive shooting dummy:
   * - Head with distinct bullseye / headshot critical zone
   * - Broad shoulders & torso chest plate
   * - Waist and lower limb silhouette
   */
  createHumanoidTarget(radius) {
    const group = new THREE.Group();

    const armorMat = new THREE.MeshStandardMaterial({
      color: this.colorScheme.outer,
      roughness: 0.35,
      metalness: 0.4
    });
    const frameMat = new THREE.MeshStandardMaterial({
      color: 0x1e293b,
      roughness: 0.5,
      metalness: 0.3
    });

    // 1. Head / Bullseye Core (Critical Headshot Zone)
    const headRadius = radius * 0.34;
    const headGeo = new THREE.SphereGeometry(headRadius, 18, 18);
    const headMat = new THREE.MeshBasicMaterial({ color: this.colorScheme.core });
    const headMesh = new THREE.Mesh(headGeo, headMat);
    headMesh.name = 'target_core';
    headMesh.position.set(0, radius * 0.95, 0);
    group.add(headMesh);

    // Glowing Tactical Visor / Eye Bar
    const visorGeo = new THREE.BoxGeometry(headRadius * 1.3, headRadius * 0.25, headRadius * 0.5);
    const visorMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const visorMesh = new THREE.Mesh(visorGeo, visorMat);
    visorMesh.position.set(0, radius * 0.96, headRadius * 0.75);
    group.add(visorMesh);

    // 2. Neck
    const neckGeo = new THREE.CylinderGeometry(radius * 0.14, radius * 0.16, radius * 0.18, 12);
    const neckMesh = new THREE.Mesh(neckGeo, frameMat);
    neckMesh.position.set(0, radius * 0.72, 0);
    group.add(neckMesh);

    // 3. Shoulders & Upper Chest
    const chestGeo = new THREE.BoxGeometry(radius * 1.45, radius * 0.55, radius * 0.48);
    const chestMesh = new THREE.Mesh(chestGeo, armorMat);
    chestMesh.name = 'target_outer';
    chestMesh.position.set(0, radius * 0.40, 0);
    group.add(chestMesh);

    // 4. Mid Torso / Abdomen
    const midTorsoGeo = new THREE.BoxGeometry(radius * 1.1, radius * 0.45, radius * 0.42);
    const midTorsoMesh = new THREE.Mesh(midTorsoGeo, frameMat);
    midTorsoMesh.position.set(0, -radius * 0.08, 0);
    group.add(midTorsoMesh);

    // 5. Pelvis / Hips
    const hipGeo = new THREE.BoxGeometry(radius * 1.0, radius * 0.35, radius * 0.40);
    const hipMesh = new THREE.Mesh(hipGeo, armorMat);
    hipMesh.position.set(0, -radius * 0.46, 0);
    group.add(hipMesh);

    // 6. Upper Legs (Dual Silhouette Pillars)
    const legGeo = new THREE.BoxGeometry(radius * 0.34, radius * 0.65, radius * 0.35);
    const leftLeg = new THREE.Mesh(legGeo, frameMat);
    leftLeg.position.set(-radius * 0.32, -radius * 0.92, 0);
    group.add(leftLeg);

    const rightLeg = new THREE.Mesh(legGeo, frameMat);
    rightLeg.position.set(radius * 0.32, -radius * 0.92, 0);
    group.add(rightLeg);

    // 7. Overall Combined Hitbox Collider
    const hitGeo = new THREE.BoxGeometry(radius * 1.55, radius * 2.6, radius * 0.85);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitMesh = new THREE.Mesh(hitGeo, hitMat);
    hitMesh.name = 'target_collider';
    hitMesh.position.set(0, 0, 0);
    group.add(hitMesh);

    group.userData = {
      coreMesh: headMesh,
      outerMesh: chestMesh,
      hitMesh,
      baseRadius: radius,
      shape: 'humanoid',
      hasHeadshot: true
    };
    return group;
  }

  createTargetMesh(radius = this.targetRadius, shapePreference = 'mixed') {
    let resolvedShape = shapePreference;
    if (resolvedShape === 'mixed' || !resolvedShape) {
      const shapes = ['humanoid', 'sphere', 'bot', 'drone', 'disc'];
      resolvedShape = shapes[Math.floor(Math.random() * shapes.length)];
    }

    switch (resolvedShape) {
      case 'humanoid':
        return this.createHumanoidTarget(radius);
      case 'bot':
        return this.createBotTarget(radius);
      case 'drone':
        return this.createDroneTarget(radius);
      case 'disc':
        return this.createDiscTarget(radius);
      case 'sphere':
      default:
        return this.createSphereTarget(radius);
    }
  }

  spawnTarget(options = {}) {
    const {
      type = 'static', // 'static' | 'tracking' | 'switching' | 'lateral' | 'reaction'
      direction = null,
      maxHp = 1,
      shape = null,
      motion = null,
      speed = 1.0
    } = options;

    const userShape = shape || this.settingsStore.get('targetShape') || 'mixed';
    const userScaleMod = Number(this.settingsStore.get('targetSize')) || 1.0;

    // Multi-Distance Depth & Range Zone Calculations
    const depthZone = this.settingsStore.get('targetRangeZone') || 'all';
    let chosenZone = depthZone;
    if (chosenZone === 'all') {
      const rand = Math.random();
      if (rand < 0.35) chosenZone = 'close';
      else if (rand < 0.80) chosenZone = 'mid';
      else chosenZone = 'long';
    }

    let defaultX = 0;
    let defaultY = 1.65;
    let defaultZ = -12;
    let zoneScaleMod = 1.0;

    if (chosenZone === 'close') {
      defaultZ = -4.8 - Math.random() * 2.6; // 4.8m to 7.4m (Close-Quarter)
      zoneScaleMod = 1.50 + Math.random() * 0.30; // Larger closer targets
      defaultX = (Math.random() - 0.5) * 5.2;
      defaultY = 1.0 + Math.random() * 1.3;
    } else if (chosenZone === 'long') {
      defaultZ = -16.0 - Math.random() * 7.0; // 16m to 23m (Long-Range precision)
      zoneScaleMod = 0.55 + Math.random() * 0.20; // Micro targets
      defaultX = (Math.random() - 0.5) * 10.5;
      defaultY = 1.2 + Math.random() * 1.8;
    } else {
      // 'mid'
      defaultZ = -9.0 - Math.random() * 5.0; // 9m to 14m (Standard duel)
      zoneScaleMod = 0.95 + Math.random() * 0.15;
      defaultX = (Math.random() - 0.5) * 8.5;
      defaultY = 1.1 + Math.random() * 1.7;
    }

    const posX = options.x !== undefined ? options.x : defaultX;
    const posY = options.y !== undefined ? options.y : defaultY;
    const posZ = options.z !== undefined ? options.z : defaultZ;
    const baseScale = options.scale !== undefined ? options.scale : 1.0;
    const finalScale = baseScale * userScaleMod * zoneScaleMod;

    const targetMesh = this.createTargetMesh(this.targetRadius * finalScale, userShape);
    targetMesh.position.set(posX, posY, posZ);
    targetMesh.scale.set(0.01, 0.01, 0.01); // Spawn scale up animation

    // Motion determination
    const movingEnabled = this.settingsStore.get('movingTargetsEnabled');
    let chosenMotion = motion || this.settingsStore.get('motionPattern') || 'auto';

    if (chosenMotion === 'auto') {
      if (type === 'tracking' || type === 'lateral') {
        chosenMotion = 'strafe';
      } else if (movingEnabled && type !== 'reaction') {
        const motions = ['strafe', 'sine', 'evasive', 'static'];
        chosenMotion = motions[Math.floor(Math.random() * motions.length)];
      } else {
        chosenMotion = 'static';
      }
    }

    const userSpeedMod = Number(this.settingsStore.get('targetSpeed')) || 1.0;
    const baseSpeed = speed * userSpeedMod;

    const id = this.nextTargetId++;
    const targetObj = {
      id,
      mesh: targetMesh,
      coreMesh: targetMesh.userData.coreMesh,
      outerMesh: targetMesh.userData.outerMesh,
      collider: targetMesh.userData.hitMesh,
      shape: targetMesh.userData.shape,
      hasHeadshot: !!targetMesh.userData.hasHeadshot,
      type,
      motion: chosenMotion,
      hp: maxHp,
      maxHp,
      spawnTime: performance.now(),
      targetScale: finalScale,
      currentScale: 0.01,
      isDespawning: false,
      isHit: false,
      // Motion Kinematics
      basePos: new THREE.Vector3(posX, posY, posZ),
      timeAlive: 0,
      velocity: new THREE.Vector3(
        (Math.random() > 0.5 ? 1 : -1) * (2.8 + Math.random() * 1.5) * baseSpeed,
        (Math.random() - 0.5) * 1.6 * baseSpeed,
        0
      ),
      sineFreqX: 1.2 + Math.random() * 0.8,
      sineFreqY: 1.8 + Math.random() * 1.2,
      sineAmpX: 1.8 + Math.random() * 1.5,
      sineAmpY: 0.6 + Math.random() * 0.6,
      jumpVelocityY: 0,
      bounds: {
        minX: -6.5,
        maxX: 6.5,
        minY: 0.8,
        maxY: 3.4
      },
      dirChangeTimer: 0.8 + Math.random() * 1.2,
      directionPrompt: direction
    };

    this.group.add(targetMesh);
    this.activeTargets.push(targetObj);
    return targetObj;
  }

  destroyTarget(targetObj, isHeadshot = false) {
    if (!targetObj || targetObj.isDespawning) return;
    targetObj.isDespawning = true;
    targetObj.isHit = true;

    // Spawn sparks at impact point
    const worldPos = new THREE.Vector3();
    targetObj.mesh.getWorldPosition(worldPos);
    this.sceneManager.spawnHitSparks(worldPos, isHeadshot ? 0xffffff : this.colorScheme.hex, 12);

    // Remove from group
    const idx = this.activeTargets.indexOf(targetObj);
    if (idx !== -1) {
      this.activeTargets.splice(idx, 1);
    }
    this.group.remove(targetObj.mesh);
  }

  update(delta) {
    for (let i = this.activeTargets.length - 1; i >= 0; i--) {
      const t = this.activeTargets[i];
      t.timeAlive += delta;

      // 1. Spawn Scale Pop-in
      if (t.currentScale < t.targetScale) {
        t.currentScale = Math.min(t.targetScale, t.currentScale + delta * 8.5);
        t.mesh.scale.set(t.currentScale, t.currentScale, t.currentScale);
      }

      // 2. Kinematic Motion Patterns
      if (t.motion === 'strafe') {
        t.mesh.position.x += t.velocity.x * delta;
        if (t.mesh.position.x <= t.bounds.minX || t.mesh.position.x >= t.bounds.maxX) {
          t.velocity.x *= -1;
          t.mesh.position.x = THREE.MathUtils.clamp(t.mesh.position.x, t.bounds.minX, t.bounds.maxX);
        }
      } else if (t.motion === 'sine') {
        t.mesh.position.x = t.basePos.x + Math.sin(t.timeAlive * t.sineFreqX) * t.sineAmpX;
        t.mesh.position.y = t.basePos.y + Math.cos(t.timeAlive * t.sineFreqY) * t.sineAmpY;
      } else if (t.motion === 'evasive') {
        t.mesh.position.addScaledVector(t.velocity, delta);
        if (t.mesh.position.x <= t.bounds.minX || t.mesh.position.x >= t.bounds.maxX) {
          t.velocity.x *= -1;
          t.mesh.position.x = THREE.MathUtils.clamp(t.mesh.position.x, t.bounds.minX, t.bounds.maxX);
        }
        if (t.mesh.position.y <= t.bounds.minY || t.mesh.position.y >= t.bounds.maxY) {
          t.velocity.y *= -1;
          t.mesh.position.y = THREE.MathUtils.clamp(t.mesh.position.y, t.bounds.minY, t.bounds.maxY);
        }

        t.dirChangeTimer -= delta;
        if (t.dirChangeTimer <= 0) {
          t.dirChangeTimer = 0.5 + Math.random() * 0.9;
          t.velocity.x = (Math.random() - 0.5) * 5.2;
          t.velocity.y = (Math.random() - 0.5) * 2.8;
        }
      }

      // Subtle rotation for drones / discs / spheres
      if (t.shape === 'drone' || t.shape === 'sphere') {
        t.mesh.rotation.z += delta * 0.8;
      }
    }
  }

  clear() {
    for (const t of this.activeTargets) {
      this.group.remove(t.mesh);
    }
    this.activeTargets = [];
  }

  getActiveCount() {
    return this.activeTargets.length;
  }

  getFirstActive() {
    return this.activeTargets[0] || null;
  }
}
